import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, Wifi, Droplets, Home, Bed, Coffee, UtensilsCrossed,
  Sun, Waves, TreePalm, Heart, Users, CreditCard, Shield,
  Package, Sparkles, Wind, Hammer, Trash2, DollarSign,
  Phone, Calendar, Key, Camera, Music, BookOpen, MapPin,
  ThermometerSun, CloudRain, Moon, Sunrise, Fish, Shell
} from 'lucide-react';

// The art of knolling: lay everything out so you can see it all at once
// Every category, every item, every system needed to run a beach pousada in Brazil

const POUSADA_UNIVERSE = {
  // Layer 1: The Bones (Infrastructure)
  infrastructure: {
    icon: Zap,
    name: 'Infrastructure',
    color: 'yellow',
    items: [
      { name: 'Electricity', critical: true, notes: 'Generator backup?' },
      { name: 'Internet/WiFi', critical: true, notes: 'Starlink? Fiber? 4G backup?' },
      { name: 'Water supply', critical: true, notes: 'Well? City? Storage tanks?' },
      { name: 'Sewage/Septic', critical: true, notes: 'Environmental compliance' },
      { name: 'Gas (cooking)', critical: true, notes: 'Propane delivery schedule' },
      { name: 'Phone/WhatsApp', critical: true, notes: 'Business number' },
      { name: 'Solar panels', critical: false, notes: 'Reduce costs, eco-friendly' },
      { name: 'Water heaters', critical: false, notes: 'Solar? Electric? Gas?' }
    ]
  },

  // Layer 2: The Shell (Physical Space)
  physical: {
    icon: Home,
    name: 'Physical Space',
    color: 'blue',
    items: [
      { name: 'Guest rooms', notes: 'How many? Types? Views?' },
      { name: 'Reception area', notes: 'First impression space' },
      { name: 'Common areas', notes: 'Where people connect' },
      { name: 'Kitchen', notes: 'Guest access? Staff only?' },
      { name: 'Storage rooms', notes: 'Linens, supplies, equipment' },
      { name: 'Laundry area', notes: 'Industrial washer? Drying space?' },
      { name: 'Parking', notes: 'How many spots? Covered?' },
      { name: 'Gardens/Landscaping', notes: 'Native plants, low maintenance' },
      { name: 'Beach access path', notes: 'Private? Shared? Distance?' },
      { name: 'Pool/Jacuzzi', notes: 'Maintenance nightmare or selling point?' },
      { name: 'Outdoor showers', notes: 'Essential for beach sand' }
    ]
  },

  // Layer 3: The Nest (Guest Comfort)
  comfort: {
    icon: Bed,
    name: 'Guest Comfort',
    color: 'purple',
    items: [
      { name: 'Beds & mattresses', notes: 'Quality = reviews' },
      { name: 'Linens & towels', notes: 'How many sets per room?' },
      { name: 'Pillows', notes: 'Firm? Soft? Options?' },
      { name: 'Air conditioning', notes: 'Essential or ceiling fans enough?' },
      { name: 'Mosquito nets', notes: 'Screens? Nets? Repellent?' },
      { name: 'Mini-fridges', notes: 'In every room?' },
      { name: 'Safes', notes: 'In-room security' },
      { name: 'Hangers', notes: 'Never enough hangers' },
      { name: 'Beach towels', notes: 'Separate from bath towels' },
      { name: 'Hammocks', notes: 'The Brazilian dream' },
      { name: 'Room darkening', notes: 'Curtains? Blinds?' },
      { name: 'Sound insulation', notes: 'Party noise management' }
    ]
  },

  // Layer 4: The Fuel (Food & Beverage)
  foodBeverage: {
    icon: Coffee,
    name: 'Food & Beverage',
    color: 'orange',
    items: [
      { name: 'Coffee setup', notes: 'Brazilian coffee culture is serious' },
      { name: 'Breakfast spread', notes: 'Fruits, breads, eggs, tapioca' },
      { name: 'Pão de queijo', notes: 'Cheese bread is mandatory' },
      { name: 'Fresh juices', notes: 'Açaí, mango, passion fruit, cashew' },
      { name: 'Tapioca station', notes: 'Live cooking experience?' },
      { name: 'Cachaça selection', notes: 'For caipirinhas' },
      { name: 'Água de coco', notes: 'Fresh coconut water' },
      { name: 'Kitchen equipment', notes: 'Industrial? Domestic?' },
      { name: 'Plates & cutlery', notes: 'Breakage budget' },
      { name: 'Bar setup', notes: 'Full bar? Beer & basics?' },
      { name: 'Snack inventory', notes: 'Chips, cookies, nuts' },
      { name: 'Special diets', notes: 'Vegan? Gluten-free?' }
    ]
  },

  // Layer 5: The Beach Life
  beachServices: {
    icon: Waves,
    name: 'Beach Services',
    color: 'cyan',
    items: [
      { name: 'Beach chairs', notes: 'How many? Quality? Storage?' },
      { name: 'Umbrellas/Shade', notes: 'Wind-resistant models' },
      { name: 'Coolers', notes: 'For guest use?' },
      { name: 'Surfboards', notes: 'Rental? Storage? Insurance?' },
      { name: 'SUP boards', notes: 'Popular, easy, profitable' },
      { name: 'Kayaks', notes: 'Singles? Doubles?' },
      { name: 'Snorkel gear', notes: 'Hygiene? Sizes?' },
      { name: 'Beach games', notes: 'Volleyball, frescobol, football' },
      { name: 'Sunscreen station', notes: 'Complimentary? For sale?' },
      { name: 'Aloe vera', notes: 'For the inevitable sunburns' },
      { name: 'Beach bags', notes: 'Branded? Reusable?' },
      { name: 'Outdoor speakers', notes: 'Bluetooth? Waterproof?' }
    ]
  },

  // Layer 6: The Experience (Activities & Services)
  experiences: {
    icon: Heart,
    name: 'Experiences',
    color: 'pink',
    items: [
      { name: 'Massage services', notes: 'In-house? Partners?' },
      { name: 'Yoga classes', notes: 'Sunrise sessions?' },
      { name: 'Boat tours', notes: 'Partner with locals?' },
      { name: 'Fishing trips', notes: 'Equipment? Guide?' },
      { name: 'Surf lessons', notes: 'Instructor on-call?' },
      { name: 'Capoeira demos', notes: 'Cultural experience' },
      { name: 'Cooking classes', notes: 'Moqueca? Caipirinha?' },
      { name: 'Live music nights', notes: 'Local musicians' },
      { name: 'Bonfire setup', notes: 'Permits? Safety? S\'mores?' },
      { name: 'Nature walks', notes: 'Guided? Maps?' },
      { name: 'Bike rentals', notes: 'Maintenance headache?' },
      { name: 'Photography spots', notes: 'Instagram guide?' }
    ]
  },

  // Layer 7: The Team
  staffing: {
    icon: Users,
    name: 'Staffing',
    color: 'green',
    items: [
      { name: 'Reception/Check-in', notes: 'English? Spanish? 24/7?' },
      { name: 'Housekeeping', notes: 'Daily? On-request?' },
      { name: 'Maintenance/Handyman', notes: 'On-site? On-call?' },
      { name: 'Cook/Kitchen', notes: 'Full-time? Breakfast only?' },
      { name: 'Bartender', notes: 'Evening only?' },
      { name: 'Security', notes: 'Night guard? Cameras?' },
      { name: 'Gardener', notes: 'Weekly? Daily?' },
      { name: 'Laundry service', notes: 'In-house? Outsourced?' },
      { name: 'Tour coordinator', notes: 'Commissions?' },
      { name: 'Social media', notes: 'Who posts the sunset pics?' },
      { name: 'Driver/Transport', notes: 'Airport pickups?' }
    ]
  },

  // Layer 8: The Business Brain
  operations: {
    icon: DollarSign,
    name: 'Business Operations',
    color: 'emerald',
    items: [
      { name: 'Booking system', notes: 'Booking.com? Airbnb? Direct?' },
      { name: 'Payment processing', notes: 'PIX, cards, cash, crypto?' },
      { name: 'Accounting software', notes: 'Tax compliance' },
      { name: 'Pricing strategy', notes: 'Seasonal? Dynamic?' },
      { name: 'Insurance policies', notes: 'Property, liability, workers' },
      { name: 'Licenses/Permits', notes: 'Tourism, health, fire' },
      { name: 'Guest database', notes: 'CRM? WhatsApp?' },
      { name: 'Review management', notes: 'Google, TripAdvisor, Booking' },
      { name: 'Revenue tracking', notes: 'Room, F&B, activities' },
      { name: 'Expense tracking', notes: 'Where\'s the money going?' },
      { name: 'Commission structure', notes: 'OTAs, partners, staff' },
      { name: 'Emergency fund', notes: 'When things break' }
    ]
  },

  // Layer 9: The Supply Chain
  supplies: {
    icon: Package,
    name: 'Supplies & Inventory',
    color: 'indigo',
    items: [
      { name: 'Toilet paper', notes: 'Never run out. Ever.' },
      { name: 'Cleaning supplies', notes: 'Eco-friendly?' },
      { name: 'Toiletries', notes: 'Soap, shampoo, conditioner' },
      { name: 'Bug spray/Repellent', notes: 'Tropical necessity' },
      { name: 'First aid kit', notes: 'Jellyfish stings, cuts' },
      { name: 'Batteries', notes: 'For remotes, flashlights' },
      { name: 'Light bulbs', notes: 'LED? Warm light?' },
      { name: 'Candles', notes: 'Power outages, ambiance' },
      { name: 'Matches/Lighters', notes: 'Bonfire, emergencies' },
      { name: 'Garbage bags', notes: 'Different sizes' },
      { name: 'Office supplies', notes: 'Pens, paper, printer' },
      { name: 'Tools', notes: 'Basic repairs' }
    ]
  },

  // Layer 10: The Vibe (Atmosphere & Marketing)
  atmosphere: {
    icon: Sparkles,
    name: 'Atmosphere & Marketing',
    color: 'violet',
    items: [
      { name: 'Music playlists', notes: 'Bossa nova? Reggae? MPB?' },
      { name: 'Lighting design', notes: 'Sunset ambiance' },
      { name: 'Scent/Aromatherapy', notes: 'Coconut? Ocean breeze?' },
      { name: 'Art & Decoration', notes: 'Local artists?' },
      { name: 'Photography', notes: 'Professional shots' },
      { name: 'Website', notes: 'Direct bookings' },
      { name: 'Instagram presence', notes: 'Daily stories' },
      { name: 'WhatsApp Business', notes: 'Quick responses' },
      { name: 'Welcome gifts', notes: 'Havaianas? Cachaça?' },
      { name: 'Guest book', notes: 'Stories & recommendations' },
      { name: 'Local guidebook', notes: 'Secret spots' },
      { name: 'Signage', notes: 'Wayfinding, rules, wifi' }
    ]
  },

  // Layer 11: The Local Ecosystem
  partnerships: {
    icon: MapPin,
    name: 'Local Partnerships',
    color: 'red',
    items: [
      { name: 'Airport transfers', notes: 'Reliable drivers' },
      { name: 'Restaurants', notes: 'Recommendations, commissions' },
      { name: 'Tour operators', notes: 'Boat, jungle, city' },
      { name: 'Massage therapists', notes: 'On-call list' },
      { name: 'Surf instructors', notes: 'Certified, insured' },
      { name: 'Taxi/Uber contacts', notes: 'Late night options' },
      { name: 'Medical clinic', notes: 'Emergency contact' },
      { name: 'Pharmacy', notes: '24-hour option?' },
      { name: 'Grocery delivery', notes: 'WhatsApp orders?' },
      { name: 'Laundry service', notes: 'Backup option' },
      { name: 'Equipment rental', notes: 'Bikes, boards, gear' },
      { name: 'Event spaces', notes: 'Weddings? Parties?' }
    ]
  },

  // Layer 12: The Seasons (Weather & Time)
  seasonal: {
    icon: ThermometerSun,
    name: 'Seasonal Considerations',
    color: 'amber',
    items: [
      { name: 'High season prep', notes: 'Dec-Mar, July' },
      { name: 'Low season strategy', notes: 'Discounts? Maintenance?' },
      { name: 'Rainy season', notes: 'Indoor activities' },
      { name: 'Hurricane/Storm prep', notes: 'Emergency protocols' },
      { name: 'Carnival period', notes: 'Pricing, availability' },
      { name: 'Holiday decorations', notes: 'Christmas, New Year' },
      { name: 'Mosquito season', notes: 'Extra protection' },
      { name: 'Jellyfish season', notes: 'Warnings, vinegar' },
      { name: 'Turtle nesting', notes: 'Light restrictions' },
      { name: 'Whale watching', notes: 'July-October highlight' },
      { name: 'Festival calendar', notes: 'Local events' },
      { name: 'School holidays', notes: 'Family focus' }
    ]
  }
};

const PousadaKnolling: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'knolling' | 'list'>('knolling');

  const getColorClasses = (color: string) => {
    const colors: { [key: string]: string } = {
      yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
      blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
      purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
      orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
      cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
      pink: 'bg-pink-500/10 border-pink-500/30 text-pink-400',
      green: 'bg-green-500/10 border-green-500/30 text-green-400',
      emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
      indigo: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400',
      violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400',
      red: 'bg-red-500/10 border-red-500/30 text-red-400',
      amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="text-5xl font-bold text-white mb-4">
            🏖️ Pousada Knolling
          </h1>
          <p className="text-xl text-gray-300 mb-2">
            Everything needed to run a beach pousada in Brazil, laid out Adam Savage style
          </p>
          <p className="text-sm text-gray-500">
            "Always be knolling" - See everything at once, understand the whole system
          </p>
        </motion.div>

        {/* View Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-800/50 rounded-lg p-1 flex gap-2">
            <button
              onClick={() => setViewMode('knolling')}
              className={`px-4 py-2 rounded transition-all ${
                viewMode === 'knolling' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Knolling View
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded transition-all ${
                viewMode === 'list' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              List View
            </button>
          </div>
        </div>

        {/* Knolling Grid */}
        {viewMode === 'knolling' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Object.entries(POUSADA_UNIVERSE).map(([key, category], index) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === key;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  onClick={() => setSelectedCategory(isSelected ? null : key)}
                  className={`
                    relative border-2 rounded-xl p-6 cursor-pointer transition-all
                    ${getColorClasses(category.color)}
                    ${isSelected ? 'ring-4 ring-white/20' : ''}
                  `}
                >
                  <div className="flex flex-col items-center text-center">
                    <Icon className="w-12 h-12 mb-3" />
                    <h3 className="font-bold text-lg mb-2">{category.name}</h3>
                    <p className="text-xs opacity-70">{category.items.length} items</p>
                    
                    {/* Preview of top items */}
                    <div className="mt-3 text-xs opacity-60">
                      {category.items.slice(0, 3).map((item, i) => (
                        <div key={i}>• {item.name}</div>
                      ))}
                      {category.items.length > 3 && <div>...</div>}
                    </div>
                  </div>

                  {/* Priority indicator */}
                  {category.items.some(item => 'critical' in item && item.critical) && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="space-y-6">
            {Object.entries(POUSADA_UNIVERSE).map(([key, category]) => {
              const Icon = category.icon;
              const isExpanded = selectedCategory === key;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`border-2 rounded-xl overflow-hidden ${getColorClasses(category.color)}`}
                >
                  <button
                    onClick={() => setSelectedCategory(isExpanded ? null : key)}
                    className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Icon className="w-8 h-8" />
                      <div className="text-left">
                        <h3 className="font-bold text-xl">{category.name}</h3>
                        <p className="text-sm opacity-70">{category.items.length} items to manage</p>
                      </div>
                    </div>
                    <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      ▼
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      className="border-t border-white/10"
                    >
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {category.items.map((item, i) => (
                          <div 
                            key={i}
                            className="flex items-start gap-3 p-3 rounded-lg bg-white/5"
                          >
                            <div className="mt-1">
                              {'critical' in item && item.critical ? (
                                <div className="w-2 h-2 bg-red-500 rounded-full" />
                              ) : (
                                <div className="w-2 h-2 bg-white/30 rounded-full" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">{item.name}</div>
                              {item.notes && (
                                <div className="text-xs opacity-60 mt-1">{item.notes}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Footer Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center text-gray-400"
        >
          <div className="flex justify-center gap-8 text-sm">
            <div>
              <span className="text-2xl font-bold text-white">
                {Object.keys(POUSADA_UNIVERSE).length}
              </span>
              <div>Categories</div>
            </div>
            <div>
              <span className="text-2xl font-bold text-white">
                {Object.values(POUSADA_UNIVERSE).reduce((acc, cat) => acc + cat.items.length, 0)}
              </span>
              <div>Total Items</div>
            </div>
            <div>
              <span className="text-2xl font-bold text-white">
                {Object.values(POUSADA_UNIVERSE).reduce((acc, cat) => 
                  acc + cat.items.filter(i => 'critical' in i && i.critical).length, 0
                )}
              </span>
              <div>Critical Items</div>
            </div>
          </div>
          
          <div className="mt-6 text-xs opacity-50">
            "First, we get organized. Then, we optimize." - Adam Savage
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default PousadaKnolling;